import React, {useState, useRef} from 'react'
import {Badge} from 'react-bootstrap'

interface TagInputProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
}

export const TagInput: React.FC<TagInputProps> = ({value, onChange, placeholder}) => {
  const [inputValue, setInputValue] = useState('')
  const [isFocused, setIsFocused] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  // Split value by semicolon and trim
  const tags = value
    ? value
        .split(';')
        .map((t) => t.trim())
        .filter(Boolean)
    : []

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      const trimmed = inputValue.trim()
      if (trimmed) {
        const parts = trimmed
          .split(';')
          .map((p) => p.trim())
          .filter(Boolean)

        const newTags = [...tags]
        parts.forEach((part) => {
          if (!newTags.includes(part)) {
            newTags.push(part)
          }
        })
        onChange(newTags.join('; '))
        setInputValue('')
      }
    } else if (e.key === 'Backspace' && !inputValue && tags.length > 0) {
      const newTags = tags.slice(0, -1)
      onChange(newTags.join('; '))
    }
  }

  const handleInputChangeLocal = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value
    if (val.includes(';')) {
      const parts = val
        .split(';')
        .map((p) => p.trim())
        .filter(Boolean)

      if (parts.length > 0) {
        const newTags = [...tags]
        parts.forEach((part) => {
          if (!newTags.includes(part)) {
            newTags.push(part)
          }
        })
        onChange(newTags.join('; '))
      }
      setInputValue('')
    } else {
      setInputValue(val)
    }
  }

  const removeTag = (indexToRemove: number) => {
    const newTags = tags.filter((_, index) => index !== indexToRemove)
    onChange(newTags.join('; '))
  }

  const handleContainerClick = () => {
    inputRef.current?.focus()
  }

  return (
    <div
      className="form-control d-flex flex-wrap gap-2 align-items-center"
      style={{
        minHeight: '100px',
        height: 'auto',
        cursor: 'text',
        alignContent: 'flex-start',
        padding: '0.5rem 0.75rem',
        backgroundColor: 'var(--bs-body-bg)',
        borderColor: isFocused ? 'var(--bs-gray-400)' : 'var(--bs-gray-300)',
        transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
      }}
      onClick={handleContainerClick}
    >
      {tags.map((tag, index) => (
        <Badge
          key={index}
          bg="light"
          text="dark"
          className="d-flex align-items-center gap-2 py-2 px-3 fw-semibold fs-7 border"
          style={{borderRadius: '0.475rem'}}
        >
          <span>{tag}</span>
          <span
            onClick={(e) => {
              e.stopPropagation()
              removeTag(index)
            }}
            style={{cursor: 'pointer'}}
            className="text-hover-danger text-muted"
          >
            <i className="fa-solid fa-xmark fs-9"></i>
          </span>
        </Badge>
      ))}
      <input
        ref={inputRef}
        type="text"
        className="border-0 flex-grow-1 p-0 fs-6 text-gray-800"
        style={{
          outline: 'none',
          minWidth: '150px',
          background: 'transparent',
          color: 'inherit',
        }}
        placeholder={tags.length === 0 ? placeholder : ''}
        value={inputValue}
        onChange={handleInputChangeLocal}
        onKeyDown={handleKeyDown}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
      />
    </div>
  )
}
